import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export interface ScoreBarItem {
  label: string;
  value: number;
  max: number;
  color?: string;
  icon?: React.ReactNode;
}

export interface ScoreBarChartProps {
  items: ScoreBarItem[];
  animated?: boolean;
}

const ScoreBarChart: React.FC<ScoreBarChartProps> = ({ items, animated = true }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {items.map((item, index) => {
        const percentage = Math.max(0, Math.min(100, (item.value / item.max) * 100));
        
        let barColor = item.color;
        if (!barColor) {
          if (percentage >= 75) barColor = '#22c55e'; // Green
          else if (percentage >= 50) barColor = '#f59e0b'; // Amber
          else barColor = '#ef4444'; // Red
        }

        return (
          <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {item.icon && <span style={{ color: '#64748b', display: 'flex' }}>{item.icon}</span>}
                {item.label}
              </span>
              <span style={{ color: '#64748b', fontWeight: 700 }}>
                {animated ? <AnimatedNumber value={item.value} /> : item.value}/{item.max}
              </span>
            </div>
            <div style={{ height: '8px', background: '#f1f5f9', borderRadius: '100px', overflow: 'hidden' }}>
              <motion.div
                initial={animated ? { width: 0 } : { width: `${percentage}%` }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 1, delay: animated ? 0.2 + index * 0.1 : 0 }}
                style={{ height: '100%', borderRadius: '100px', background: barColor }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Sub-component for animated number count-up
const AnimatedNumber: React.FC<{ value: number }> = ({ value }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const increment = value / (duration / 16);
    
    if (value === 0) {
      setDisplayValue(0);
      return;
    }

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        // Handle decimals if the value has decimals
        setDisplayValue(Number.isInteger(value) ? Math.floor(start) : Number(start.toFixed(1)));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return <span>{displayValue}</span>;
};

export default ScoreBarChart;
